import React, { useState } from 'react';
import { Card, Input, Button, Space, message, Form, Tag, Checkbox, Row, Col, Modal, Tooltip, Alert, Divider, Typography, ConfigProvider, theme } from 'antd';
import { EditOutlined, KeyOutlined, UserAddOutlined, LockOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { MODULE_IDS } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';

import { getUsers, createUser, updateUser, resetPassword, getAllModules } from '../../api/users.api';

import DataTable from '../../components/common/DataTable';
import FormDrawer from '../../components/common/FormDrawer';

const { Text } = Typography;

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-user-mgmt-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-user-mgmt-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 12px 16px;
      margin-bottom: 16px;
    }

    .light-user-mgmt-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      padding: 6px 10px !important;
      border-bottom: 1px solid var(--border-hi) !important;
    }

    .light-user-mgmt-page .ant-table-tbody > tr > td {
      padding: 6px 10px !important;
      font-size: 13px;
      color: var(--text-1);
      border-bottom: 1px solid var(--border);
    }

    /* Drawer Form Styling */
    .drawer-form-label label {
      font-size: 11px !important;
      font-weight: 600;
      color: var(--text-2);
      text-transform: uppercase;
    }
  `}</style>
);

const UserManagementPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetModal, setResetModal] = useState({ open: false, loginId: null });
  const [resetPasswordValue, setResetPasswordValue] = useState('');

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();
  const { canAdd, canEdit } = usePermissions(MODULE_IDS.USER_MGMT);

  const { control, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      loginID: '', user_Name: '', password: '', mobileNo: '', isActive: true, moduleList: []
    }
  });

  // --- Queries ---
  const { data: usersResponse, isLoading: isUsersLoading } = useQuery({
    queryKey: ['users', plantId],
    queryFn: () => getUsers(plantId),
    enabled: !!plantId
  });

  const usersData = usersResponse?.items || [];

  const { data: modulesData } = useQuery({
    queryKey: ['allModules'],
    queryFn: getAllModules
  });

  // --- Mutations ---
  const saveMutation = useMutation({
    mutationFn: (data) => editingUser 
      ? updateUser(plantId, editingUser.loginID, data) 
      : createUser(plantId, data),
    onSuccess: () => {
      message.success(`User ${editingUser ? 'updated' : 'created'} successfully!`);
      queryClient.invalidateQueries(['users']);
      closeDrawer();
    },
    onError: (err) => message.error(err.message || "Operation failed")
  });

  const resetPassMutation = useMutation({
    mutationFn: () => resetPassword(plantId, resetModal.loginId, resetPasswordValue),
    onSuccess: () => {
      message.success('Password has been reset successfully');
      setResetModal({ open: false, loginId: null });
      setResetPasswordValue('');
    }
  });

  // --- Handlers ---
  const openDrawer = (record = null) => {
    setEditingUser(record);
    if (record) {
      reset({
        loginID: record.loginID,
        user_Name: record.user_Name,
        password: '',
        mobileNo: record.mobileNo || '',
        isActive: record.isActive,
        moduleList: record.moduleList || []
      });
    } else {
      reset({ loginID: '', user_Name: '', password: '', mobileNo: '', isActive: true, moduleList: [] });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingUser(null);
  };

  const onSubmit = (data) => saveMutation.mutate(data);

  // --- Columns ---
  const columns = [
    { title: 'Login ID', dataIndex: 'loginID', key: 'loginID', render: (text) => <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{text}</span> },
    { title: 'User Name', dataIndex: 'user_Name', key: 'user_Name' },
    { title: 'Mobile', dataIndex: 'mobileNo', key: 'mobileNo', render: v => <span style={{ color: 'var(--text-3)' }}>{v || '-'}</span> },
    { 
      title: 'Module Access', 
      dataIndex: 'moduleList', 
      render: (list) => <Tag bordered={false} color="blue" style={{ fontWeight: 600 }}>{list?.length || 0} Modules</Tag> 
    },
    { title: 'Created', dataIndex: 'createdOn', render: v => <span style={{ color: 'var(--text-3)' }}>{formatDate(v)}</span> },
    { 
      title: 'Status', 
      dataIndex: 'isActive', 
      render: (active) => <Tag bordered={false} color={active ? 'success' : 'error'} style={{ fontWeight: 600 }}>{active ? 'Active' : 'Inactive'}</Tag> 
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          {canEdit && (
            <>
              <Tooltip title="Edit User & Access">
                <Button type="text" size="small" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openDrawer(record)} />
              </Tooltip>
              <Tooltip title="Reset Password">
                <Button type="text" size="small" icon={<KeyOutlined style={{ color: '#f59e0b' }} />} onClick={() => setResetModal({ open: true, loginId: record.loginID })} />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-user-mgmt-page" style={{ padding: '8px 16px' }}>
        
        <Card
          className="report-card"
          styles={{ 
            header: { padding: '10px 16px', minHeight: 40, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }, 
            body: { padding: '16px' } 
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TeamOutlined style={{ color: 'var(--text-3)' }} />
              <Space direction="vertical" size={0}>
                <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-1)', lineHeight: 1 }}>
                  User Management
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>
                  Control who can access the system and their module rights
                </span>
              </Space>
            </div>
          }
          extra={
            canAdd && (
              <Button type="primary" size="small" icon={<UserAddOutlined />} onClick={() => openDrawer()} style={{ background: '#0f172a' }}>
                Create New User
              </Button>
            )
          }
        >
          <div style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
            <DataTable 
              columns={columns} 
              data={usersData || []} 
              loading={isUsersLoading} 
              rowKey="loginID" 
              size="small"
              pagination={{ pageSize: 15, size: 'small' }}
              scroll={{ x: 700 }}
            />
          </div>
        </Card>

        {/* FORM DRAWER */}
        <FormDrawer
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserAddOutlined style={{ color: '#0f172a' }} />
              <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 14 }}>
                {editingUser ? "Edit User Access" : "Create New User"}
              </span>
            </div>
          }
          open={drawerOpen}
          onClose={closeDrawer}
          onSave={handleSubmit(onSubmit)}
          loading={saveMutation.isPending}
          isDirty={isDirty}
          width={550}
        >
          {/* We wrap Drawer content to inherit global styles gracefully */}
          <div className="light-user-mgmt-page">
            <Form layout="vertical">
              
              <div className="compact-section">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Login ID" required help={errors.loginID?.message} validateStatus={errors.loginID ? 'error' : ''} className="drawer-form-label" style={{ marginBottom: 12 }}>
                      <Controller
                        name="loginID"
                        control={control}
                        rules={{ required: 'Required' }}
                        render={({ field }) => <Input size="small" {...field} disabled={!!editingUser} placeholder="e.g. rahul_admin" style={{ borderRadius: 4 }} />}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Full Name" required help={errors.user_Name?.message} validateStatus={errors.user_Name ? 'error' : ''} className="drawer-form-label" style={{ marginBottom: 12 }}>
                      <Controller
                        name="user_Name"
                        control={control}
                        rules={{ required: 'Required' }}
                        render={({ field }) => <Input size="small" {...field} placeholder="Enter user's name" style={{ borderRadius: 4 }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  {!editingUser && (
                    <Col span={12}>
                      <Form.Item label="Initial Password" required help={errors.password?.message} validateStatus={errors.password ? 'error' : ''} className="drawer-form-label" style={{ marginBottom: 0 }}>
                        <Controller
                          name="password"
                          control={control}
                          rules={{ required: 'Password is required' }}
                          render={({ field }) => <Input.Password size="small" {...field} prefix={<LockOutlined style={{ color: 'var(--text-3)' }}/>} style={{ borderRadius: 4 }} />}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={12}>
                    <Form.Item label="Mobile Number" help={errors.mobileNo?.message} validateStatus={errors.mobileNo ? 'error' : ''} className="drawer-form-label" style={{ marginBottom: 0 }}>
                      <Controller
                        name="mobileNo"
                        control={control}
                        rules={{ pattern: { value: /^[0-9]{10}$/, message: '10 digits only' } }}
                        render={({ field }) => <Input size="small" {...field} maxLength={10} placeholder="10-digit mobile" style={{ borderRadius: 4 }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Divider orientation="left" style={{ margin: '24px 0 16px 0', borderColor: '#e2e8f0', fontSize: 12, color: 'var(--text-3)', fontWeight: 600 }}>
                MODULE ACCESS RIGHTS
              </Divider>
              
              <Alert 
                message="Select the modules this user is permitted to access." 
                type="info" showIcon 
                style={{ marginBottom: 16, background: '#f1f5f9', border: '1px solid #cbd5e1', color: 'var(--text-2)' }} 
              />
              
              <div className="compact-section" style={{ background: '#fff' }}>
                <Form.Item name="moduleList" style={{ marginBottom: 0 }}>
                  <Controller
                    name="moduleList"
                    control={control}
                    render={({ field }) => (
                      <Checkbox.Group {...field} style={{ width: '100%' }}>
                        <Row gutter={[16, 16]}>
                          {modulesData?.map(mod => (
                            <Col span={12} key={mod.mid}>
                              <Checkbox value={mod.mid} style={{ color: 'var(--text-1)', fontSize: 13, fontWeight: 500 }}>
                                {mod.modules}
                              </Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    )}
                  />
                </Form.Item>
              </div>

            </Form>
          </div>
        </FormDrawer>

        {/* RESET PASSWORD MODAL */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <KeyOutlined style={{ color: '#f59e0b' }} />
              <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 14 }}>Reset Password</span>
            </div>
          }
          open={resetModal.open}
          onOk={() => resetPassMutation.mutate()}
          onCancel={() => setResetModal({ open: false, loginId: null })}
          confirmLoading={resetPassMutation.isPending}
          okText="Update Password"
          okButtonProps={{ style: { background: '#0f172a' } }}
        >
          <div className="light-user-mgmt-page">
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Resetting password for: <b style={{ color: 'var(--text-1)' }}>{resetModal.loginId}</b></Text>
            </div>
            <Alert 
              message="Security Note" 
              description="Passwords are encrypted. Setting a new one here will overwrite the old password immediately." 
              type="warning" showIcon 
              style={{ marginBottom: 16, fontSize: 12 }} 
            />
            <Input.Password 
              size="large"
              placeholder="Enter new strong password" 
              value={resetPasswordValue}
              onChange={(e) => setResetPasswordValue(e.target.value)}
              prefix={<LockOutlined style={{ color: '#cbd5e1' }} />}
              style={{ borderRadius: 6 }}
            />
          </div>
        </Modal>

      </div>
    </ConfigProvider>
  );
};

export default UserManagementPage;